import type { MenuItem } from '@ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRowMenu, useLoadedForAction } from '../../../app/rowActions';
import { useSession } from '../../identity/session';
import { useBatch } from '../api';
import { BatchFormDialog } from './BatchFormDialog';
import { CorrectionDialog } from './CorrectionDialog';
import { FillingDialog } from './FillingDialog';

type Kind = 'fill' | 'edit' | 'correct';

/**
 * Quick actions on a batch from a row's `…` menu (#197): fill jars, edit
 * and correct the stock, with the dialogs of the batch page.
 */
export function useBatchActions(): {
  /** Menu items for a batch; `bulkGrams` decides whether filling is possible. */
  items: (batchId: string, options?: { bulkGrams?: number; edit?: boolean }) => MenuItem[];
  dialogs: ReactNode;
} {
  const { t } = useTranslation();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const [open, setOpen] = useState<{ kind: Kind; id: string } | null>(null);
  const close = () => setOpen(null);
  const items = (
    id: string,
    { bulkGrams, edit = true }: { bulkGrams?: number; edit?: boolean } = {},
  ) =>
    [
      can('stock.write') &&
        (bulkGrams === undefined || bulkGrams > 0) && {
          key: 'fill',
          label: t('harvest.filling.action'),
          onSelect: () => setOpen({ kind: 'fill', id }),
        },
      edit && can('harvest.write') && rowMenu.edit(() => setOpen({ kind: 'edit', id })),
      // The correction dialog loads the batch, which needs `harvest.read`.
      can('stock.write') &&
        can('harvest.read') && {
          key: 'correct',
          label: t('harvest.correction.title'),
          onSelect: () => setOpen({ kind: 'correct', id }),
        },
    ].filter((i): i is MenuItem => Boolean(i));
  const dialogs = (
    <>
      {open?.kind === 'fill' && <FillingDialog open batchId={open.id} onClose={close} />}
      {open && open.kind !== 'fill' && (
        <LoadedBatchDialog kind={open.kind} id={open.id} onClose={close} />
      )}
    </>
  );
  return { items, dialogs };
}

/** Loads the full batch, then shows its edit or correction dialog. */
function LoadedBatchDialog({
  kind,
  id,
  onClose,
}: {
  kind: 'edit' | 'correct';
  id: string;
  onClose: () => void;
}) {
  const batch = useLoadedForAction(useBatch(id), onClose);
  if (!batch) return null;
  return kind === 'edit' ? (
    <BatchFormDialog open batch={batch} onClose={onClose} />
  ) : (
    <CorrectionDialog open batch={batch} onClose={onClose} />
  );
}
